import { FormikProps } from 'formik';
import { Grid, Typography } from '@mui/material';
import { useMemo } from 'react';

import { CommonButtonWrapper, PageContent } from '@styles/CommonStyles';
import { FormPart } from '@modules/shared/components/FormPart';

import { formatDate, getOptions } from '@shared/commonFunctions';
import { dateFormatWithTime } from '@shared/constants/formats';
import { CancelButton, SaveButton } from '@shared/inputs/Buttons';
import { GridItemsWrapper } from '@styles/ContactManager/ContactStyles';

import { SelectAutocompleteField } from '@shared/fields/SelectAutocompleteField';
import { IAssetOption } from '../../Assets/interfaces';
import { FieldLabelProps, ILead, ILeadSource } from '../types/interfaces/interfaces';

interface ILeadProps extends Partial<FormikProps<ILead>> {
  handleClose: () => void;
  value: ILead;
  assetOptions: IAssetOption[];
  secondarySources: ILeadSource[];
  isLoading: boolean;
}

const FieldLabel = ({ label, value }: FieldLabelProps) => (
  <>
    <Typography variant="subtitle1" display="inline">
      {label}
    </Typography>
    <Typography variant="subtitle2" display="inline">
      : {value}
    </Typography>
  </>
);

export const LeadForm = ({ handleClose, value, assetOptions, secondarySources, isLoading }: ILeadProps) => {
  const formConfig = useMemo(
    () => [
      {
        width: '50%',
        label: 'Id',
        value: value.numericId.toString(),
        component: FieldLabel,
      },
      {
        width: '50%',
        value: value.primarySourceName,
        label: 'Primary Source',
        component: FieldLabel,
      },
      {
        width: '50%',
        name: 'assetId',
        label: 'Asset',
        options: getOptions(assetOptions, 'id', 'display'),
        component: SelectAutocompleteField,
      },
      {
        width: '50%',
        name: 'secondarySourceId',
        label: 'Secondary Source',
        options: getOptions(secondarySources),
        component: SelectAutocompleteField,
      },
      {
        width: '50%',
        value: value.noOfNotes,
        label: 'Comms',
        component: FieldLabel,
      },
      {
        width: '50%',
        value: formatDate(value.lastNoteDateTime, { format: dateFormatWithTime }),
        label: 'Last Comm',
        component: FieldLabel,
      },
      {
        width: '50%',
        value: value.currentWorkFlowStepName,
        label: 'Workflow Step',
        component: FieldLabel,
      },
      {
        width: '50%',
        value: formatDate(value.currentWorkFlowStepDate, { format: dateFormatWithTime }),
        label: 'Step Date',
        component: FieldLabel,
      },
      {
        width: '50%',
        value: value?.assignedUser?.name,
        label: 'User',
        component: FieldLabel,
      },
      {
        width: '50%',
        value: value.lastActivity,
        label: 'Last Activity',
        component: FieldLabel,
      },
      {
        width: '50%',
        value: formatDate(value.lastActivityDate, { format: dateFormatWithTime }),
        label: 'Activity Date',
        component: FieldLabel,
      },
    ],
    [assetOptions, value, secondarySources]
  );

  return (
    <>
      <PageContent>
        <Grid container spacing={12}>
          <GridItemsWrapper item md={12}>
            <FormPart config={formConfig} />
          </GridItemsWrapper>
        </Grid>
      </PageContent>

      <CommonButtonWrapper>
        <CancelButton onClick={handleClose} />
        <SaveButton isLoading={isLoading}/>
      </CommonButtonWrapper>
    </>
  );
};
